import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/CartSummary.scss";
import { ordersApi } from "../api/ordersApi";
import { useTelegram } from "@/app/providers/TelegramProvider";
import { useMax } from "@/app/providers/MaxProvider";
import { useRuntimeMessenger } from "@/app/providers/RuntimeMessengerContext";
import { useCity } from "@/app/providers/CityProvider";
import { CartItem, CheckoutPaymentMethod, Order } from "../types";
import { formatPhone, formatPrice } from "../utils/formatter";
import { getOrderLineUnitPrice } from "../utils/productPricing";
import { userApi } from "../api/userApi";
import { useCart } from "@/app/providers/CartProvider";
import { openPaymentUrl } from "../utils/openPaymentUrl";
import { showAppAlert } from "../utils/appDialog";
import {
  requestTelegramContactPhone,
  type TelegramContactRequestHost,
} from "../utils/telegramRequestContact";
import {
  DEFAULT_SHOP_DELIVERY_CONFIG,
  fetchShopDeliveryConfig,
  type BerryBoxTier,
  type ShopDeliveryConfig,
} from "../api/shopConfigApi";
import { districtsApi, type CityDistrict } from "../api/districtsApi";

interface CartSummaryProps {
  totalAmount: number;
  itemsCount: number;
  onCheckout: () => void;
  isCheckingOut: boolean;
  minOrderAmount?: number;
}

function formatBerryBoxesLabel(tier: BerryBoxTier): string {
  if (tier.max == null) {
    return `От ${tier.min} ящ.`;
  }
  return `${tier.min}–${tier.max} ящ.`;
}

export const CartSummary: React.FC<CartSummaryProps> = ({
  totalAmount,
  itemsCount,
  onCheckout,
  isCheckingOut,
  minOrderAmount = Number(import.meta.env.VITE_MIN_ORDER_AMOUNT ?? 0),
}) => {
  const navigate = useNavigate();
  const { items: cartItems, savePendingOrderDraft, clearCart, isCartScopeMismatch } = useCart();
  const telegram = useTelegram();
  const max = useMax();
  const runtime = useRuntimeMessenger();
  const { selectedCity } = useCity();

  const activePlatform = runtime.kind;

  const messengerUserIdForDb = useMemo(() => {
    if (runtime.kind === "telegram") {
      return runtime.telegramUser?.id ?? telegram?.user?.id;
    }
    if (runtime.kind === "max") {
      return runtime.maxUser?.id ?? max?.user?.id;
    }
    return undefined;
  }, [
    runtime.kind,
    runtime.telegramUser?.id,
    runtime.maxUser?.id,
    telegram?.user?.id,
    max?.user?.id,
  ]);

  const [showForm, setShowForm] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [deliveryType, setDeliveryType] = useState<"delivery" | "pickup">(
    "pickup"
  );
  const [paymentMethod, setPaymentMethod] =
    useState<CheckoutPaymentMethod>("yookassa");

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isRequestingPhone, setIsRequestingPhone] = useState(false);
  const [manualInputMode, setManualInputMode] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [userSaved, setUserSaved] = useState(false);
  const [hasPhoneInDB, setHasPhoneInDB] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [deliveryInfoOpen, setDeliveryInfoOpen] = useState(false);
  const [deliveryCfg, setDeliveryCfg] = useState<ShopDeliveryConfig>(
    DEFAULT_SHOP_DELIVERY_CONFIG
  );
  const [districts, setDistricts] = useState<CityDistrict[]>([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [districtsError, setDistrictsError] = useState<string | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState("");

  const [formData, setFormData] = useState({
    fullName: "",
    address: "",
    phone: "",
    comment: "",
  });

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const didAutofocusAddressRef = useRef(false);
  const placingOrderRef = useRef(false);

  const isPickup = deliveryType === "pickup";
  const onlyPickup = Boolean(selectedCity?.onlyPickup);
  const isOnlinePayment = paymentMethod === "yookassa";
  const cashPaymentLabel = isPickup ? "На месте" : "При получении";

  /** Доставка оплачивается курьеру отдельно; в онлайн-оплату не включаем. */
  const finalAmount = totalAmount;
  const hasMinOrderAmount = minOrderAmount > 0;
  const isBelowMinOrder = hasMinOrderAmount && totalAmount < minOrderAmount;
  const remainingForMinOrder = Math.max(minOrderAmount - totalAmount, 0);
  const isValidPhone = formData.phone
    ? formData.phone.replace(/\D/g, "").length === 11
    : false;
  const requiresDistrict = !isPickup && districts.length > 0;
  // Пока районы грузятся / ошибка загрузки — не даём оформить доставку без выбора.
  // Иначе при districts=[] (ошибка API) заказ уходит без района.
  const isDistrictValid =
    isPickup ||
    (!districtsLoading &&
      !districtsError &&
      (!requiresDistrict || Boolean(selectedDistrictId)));
  const isFormValid =
    Boolean(formData.fullName) &&
    Boolean(isValidPhone) &&
    (isPickup || Boolean(formData.address)) &&
    isDistrictValid;

  const getCurrentUser = useCallback(() => {
    if (activePlatform === "telegram") {
      return (
        runtime.telegramUser ??
        telegram?.user ??
        window.Telegram?.WebApp?.initDataUnsafe?.user ??
        null
      );
    }
    if (activePlatform === "max") {
      return (
        runtime.maxUser ??
        max?.user ??
        window.WebApp?.initDataUnsafe?.user ??
        null
      );
    }
    return null;
  }, [activePlatform, runtime.telegramUser, runtime.maxUser, telegram?.user, max?.user]);

  const saveUserToDB = async (phoneNumber: string) => {
    if (activePlatform === "web") return;

    try {
      let userData: Parameters<typeof userApi.createOrUpdateUser>[0] | null =
        null;
      const currentUser = getCurrentUser();

      if (activePlatform === "telegram" && currentUser?.id) {
        userData = {
          messenger_type: "telegram",
          messenger_id: String(currentUser.id),
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
          username: currentUser.username,
          language_code: currentUser.language_code,
          phone: phoneNumber.replace(/\D/g, ""),
        };
      } else if (activePlatform === "max" && currentUser?.id) {
        userData = {
          messenger_type: "max",
          max_id: String(currentUser.id),
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
          username: currentUser.username,
          language_code: currentUser.language_code,
          phone: phoneNumber.replace(/\D/g, ""),
        };
      }

      if (userData) {
        await userApi.createOrUpdateUser(userData);
        console.log("User saved to DB:", userData);
        setUserSaved(true);
        setHasPhoneInDB(true);
      }
    } catch (error) {
      console.error("Failed to save user:", error);
    }
  };

  useEffect(() => {
    if (!showForm) return;
    let cancelled = false;
    fetchShopDeliveryConfig().then((cfg) => {
      if (!cancelled) setDeliveryCfg(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, [showForm]);

  const loadDistricts = useCallback(async () => {
    const outletId = selectedCity?.outletId;
    if (!outletId || isPickup) {
      setDistricts([]);
      setDistrictsError(null);
      setSelectedDistrictId("");
      return;
    }

    setDistrictsLoading(true);
    setDistrictsError(null);

    try {
      const data = await districtsApi.getDistricts(outletId);
      setDistricts(data);
      setSelectedDistrictId((prev) =>
        prev && data.some((district) => district.id === prev) ? prev : ""
      );
    } catch (error) {
      console.error("Failed to load districts:", error);
      setDistricts([]);
      setSelectedDistrictId("");
      setDistrictsError("Не удалось загрузить список районов");
    } finally {
      setDistrictsLoading(false);
    }
  }, [isPickup, selectedCity?.outletId]);

  useEffect(() => {
    if (!showForm || isPickup) {
      setDistricts([]);
      setDistrictsError(null);
      setSelectedDistrictId("");
      return;
    }
    void loadDistricts();
  }, [showForm, isPickup, loadDistricts]);

  useEffect(() => {
    if (isPickup) setDeliveryInfoOpen(false);
  }, [isPickup]);

  useEffect(() => {
    if (onlyPickup) {
      setDeliveryType("pickup");
    }
  }, [onlyPickup, selectedCity?.id]);

  useEffect(() => {
    const fetchUserFromDB = async () => {
      if (!showForm || activePlatform === "web") return;

      const messengerNumericId =
        messengerUserIdForDb ?? getCurrentUser()?.id ?? undefined;
      if (messengerNumericId == null) return;

      setIsLoadingUser(true);

      try {
        if (activePlatform === "telegram") {
          const dbUser = await userApi.getUserByMessenger(
            "telegram",
            messengerNumericId
          );
          if (dbUser) {
            const fullName = `${dbUser.first_name || ""} ${
              dbUser.last_name || ""
            }`.trim();
            const phone = dbUser.phone ? formatPhone(dbUser.phone) : "";

            setFormData((prev) => ({
              ...prev,
              fullName: prev.fullName || fullName,
              phone: prev.phone || phone,
            }));

            if (dbUser.phone) {
              setHasPhoneInDB(true);
              setUserSaved(true);
            }
          }
        } else if (activePlatform === "max") {
          const dbUser = await userApi.getUserByMessenger(
            "max",
            undefined,
            messengerNumericId
          );
          if (dbUser) {
            const fullName = `${dbUser.first_name || ""} ${
              dbUser.last_name || ""
            }`.trim();
            const phone = dbUser.phone ? formatPhone(dbUser.phone) : "";

            setFormData((prev) => ({
              ...prev,
              fullName: prev.fullName || fullName,
              phone: prev.phone || phone,
            }));

            if (dbUser.phone) {
              setHasPhoneInDB(true);
              setUserSaved(true);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch user:", error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUserFromDB();
  }, [showForm, activePlatform, messengerUserIdForDb, getCurrentUser]);

  useEffect(() => {
    if (userSaved) return;

    const currentUser = getCurrentUser();

    if (currentUser && showForm && !isLoadingUser) {
      const firstName = currentUser.first_name || "";
      const lastName = currentUser.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();

      setFormData((prev) => ({
        ...prev,
        fullName: prev.fullName || fullName,
      }));
    }
  }, [
    telegram,
    max,
    showForm,
    isLoadingUser,
    userSaved,
    activePlatform,
    runtime,
    getCurrentUser,
  ]);

  useEffect(() => {
    if (
      showForm &&
      activePlatform !== "web" &&
      !hasPhoneInDB &&
      !formData.phone &&
      !manualInputMode &&
      !showPhoneModal &&
      !isLoadingUser &&
      !userSaved
    ) {
      setShowPhoneModal(true);
    }
  }, [
    showForm,
    activePlatform,
    formData.phone,
    hasPhoneInDB,
    manualInputMode,
    isLoadingUser,
    userSaved,
    showPhoneModal,
    getCurrentUser,
  ]);

  useEffect(() => {
    if (showForm) {
      if (manualInputMode && !formData.phone) {
        setTimeout(() => phoneInputRef.current?.focus(), 100);
      } else if (
        isValidPhone &&
        !isPickup &&
        !formData.address &&
        !didAutofocusAddressRef.current
      ) {
        didAutofocusAddressRef.current = true;
        setTimeout(() => addressInputRef.current?.focus(), 100);
      }
    } else {
      didAutofocusAddressRef.current = false;
    }
  }, [
    showForm,
    manualInputMode,
    isValidPhone,
    isPickup,
    formData.address,
    formData.phone,
  ]);

  const requestPhoneNumber = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Timeout waiting for phone number"));
      }, 30000);

      if (activePlatform === "telegram") {
        const tg = window.Telegram?.WebApp;
        if (!tg) {
          clearTimeout(timeoutId);
          reject(new Error("Telegram WebApp not found"));
          return;
        }

        if (typeof tg.requestContact === "function") {
          requestTelegramContactPhone(tg as TelegramContactRequestHost, 30000)
            .then((phone) => {
              clearTimeout(timeoutId);
              resolve(phone);
            })
            .catch((err: unknown) => {
              clearTimeout(timeoutId);
              reject(err);
            });
        } else {
          clearTimeout(timeoutId);
          reject(new Error("requestContact not available"));
        }
      } else if (activePlatform === "max") {
        const maxApp = window.WebApp;
        if (!maxApp) {
          clearTimeout(timeoutId);
          reject(new Error("Max WebApp not found"));
          return;
        }

        if (typeof maxApp.requestContact === "function") {
          maxApp
            .requestContact()
            .then((result: unknown) => {
              clearTimeout(timeoutId);

              let phone: string = "";

              if (typeof result === "string") {
                phone = result;
              } else if (result && typeof result === "object") {
                const r = result as Record<string, unknown>;
                const contact = r.contact as Record<string, unknown> | undefined;
                const raw =
                  r.phone ??
                  r.phone_number ??
                  r.phoneNumber ??
                  contact?.phone_number ??
                  contact?.phone ??
                  "";
                phone = typeof raw === "string" ? raw : String(raw ?? "");
              }

              phone = phone.replace(/[^\d+]/g, "");

              if (phone) {
                console.log("Phone received from Max:", phone);
                resolve(phone);
              } else {
                console.error("No phone number in response:", result);
                reject(new Error("No phone number received"));
              }
            })
            .catch((err: unknown) => {
              clearTimeout(timeoutId);
              console.error("Max requestContact error:", err);
              reject(err instanceof Error ? err : new Error(String(err)));
            });
        } else {
          clearTimeout(timeoutId);
          reject(new Error("requestContact not available in Max"));
        }
      } else {
        clearTimeout(timeoutId);
        reject(new Error("No platform detected"));
      }
    });
  };

  const handleRequestNativePhone = async () => {
    if (isRequestingPhone) return;

    setIsRequestingPhone(true);

    try {
      const phoneNumber = await requestPhoneNumber();
      const formatted = formatPhone(phoneNumber);

      setFormData((prev) => ({ ...prev, phone: formatted }));

      await saveUserToDB(phoneNumber);

      setShowPhoneModal(false);
      setManualInputMode(false);
    } catch (error) {
      console.error("Phone request failed:", error);
      setManualInputMode(true);
      setShowPhoneModal(false);
    } finally {
      setIsRequestingPhone(false);
    }
  };

  const handleContinueManual = () => {
    setManualInputMode(true);
    setShowPhoneModal(false);
  };

  const handlePhoneBlur = async () => {
    if (
      formData.phone &&
      isValidPhone &&
      activePlatform !== "web" &&
      !hasPhoneInDB
    ) {
      await saveUserToDB(formData.phone);
    }
  };

  const handleHeaderClick = () => {
    if (!showForm) {
      setIsAnimating(true);
      setShowForm(true);
    }
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showForm) {
      setIsAnimating(false);
      setTimeout(() => setShowForm(false), 300);
    } else {
      setIsAnimating(true);
      setShowForm(true);
    }
  };

  const handleClearCartAndGoHome = () => {
    clearCart();
    navigate("/");
  };

  const handleInputChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const formatted = formatPhone(value);
      setFormData((prev) => ({ ...prev, phone: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckout = async () => {
    if (!isFormValid || isBelowMinOrder) return;
    if (placingOrderRef.current || isPlacingOrder) return;

    if (!selectedCity?.outletId) {
      void showAppAlert("Выберите торговую точку");
      return;
    }

    if (!isPickup && (districtsLoading || districtsError)) {
      void showAppAlert(
        districtsError ||
          "Дождитесь загрузки районов доставки или нажмите «Повторить»."
      );
      return;
    }

    if (!isPickup && districts.length > 0 && !selectedDistrictId) {
      void showAppAlert("Выберите район доставки");
      return;
    }

    if (selectedCity?.id && isCartScopeMismatch(selectedCity.id)) {
      void showAppAlert(
        "Корзина относится к другой торговой точке. Очистите корзину и добавьте товары заново."
      );
      return;
    }

    if (activePlatform !== "web") {
      const mid = messengerUserIdForDb;
      if (
        mid == null ||
        typeof mid !== "number" ||
        !Number.isFinite(mid) ||
        mid <= 0
      ) {
        void showAppAlert(
          "Не удалось определить профиль в приложении. Закройте и снова откройте магазин из бота, затем повторите заказ."
        );
        return;
      }
    }

    const tg = window.Telegram?.WebApp;
    const showTgBtnProgress =
      activePlatform === "telegram" &&
      typeof tg?.MainButton?.showProgress === "function" &&
      typeof tg?.MainButton?.hideProgress === "function";

    placingOrderRef.current = true;
    setIsPlacingOrder(true);
    if (showTgBtnProgress) {
      tg?.MainButton.showProgress?.(true);
    }

    try {
      if (
        formData.phone &&
        isValidPhone &&
        activePlatform !== "web" &&
        !hasPhoneInDB
      ) {
        await saveUserToDB(formData.phone);
      }

      const selectedDistrict = districts.find(
        (district) => district.id === selectedDistrictId
      );

      const orderPayload: Omit<Order, "id" | "date" | "status"> = {
        paymentMethod,
        cashId: selectedCity?.id,
        outletId: selectedCity?.outletId ?? undefined,
        pickup: isPickup,
        deliveryAddress: isPickup ? "" : formData.address,
        districtId:
          !isPickup && selectedDistrict ? selectedDistrict.id : undefined,
        districtName:
          !isPickup && selectedDistrict ? selectedDistrict.name : undefined,
        totalAmount: finalAmount,
        phone: formData.phone.replace(/\D/g, ""),
        fullName: formData.fullName.trim(),
        comment: isPickup ? "" : formData.comment?.trim(),
        items: cartItems.map((item: CartItem) => ({
          product: {
            id: item.product.id,
            name: item.product.name || "",
            price: getOrderLineUnitPrice(item.product, item.quantity),
            description: item.product.description || "",
            categoryId: item.product.categoryId || "",
            photo: item.product.photo || "",
            article: item.product.article || "",
            outcode: item.product.outcode || "",
            bonusFree: item.product.bonusFree || 0,
          },
          quantity: item.quantity,
        })),
      };

      const result = await ordersApi.createOrder(orderPayload);
      if (result && result.id) {
        if (result.districtSyncWarning) {
          await showAppAlert(result.districtSyncWarning);
        }

        if (result.paymentUrl) {
          savePendingOrderDraft({
            orderId: result.id,
            idZakaz: result.idZakaz,
            paymentUrl: result.paymentUrl,
            items: cartItems,
            checkoutForm: {
              fullName: formData.fullName,
              address: formData.address,
              phone: formData.phone,
              comment: isPickup ? "" : formData.comment,
            },
          });
          clearCart();
          openPaymentUrl(result.paymentUrl);
          // Не уходим в каталог: пользователь возвращается из оплаты в тот же экран корзины / баннер «оплатить».
        } else {
          clearCart();
          const orderLabel = result.idZakaz || result.id;
          await showAppAlert(
            isPickup
              ? `Заказ #${orderLabel} оформлен. Оплата на месте при получении.`
              : `Заказ #${orderLabel} оформлен. Оплата при получении заказа.`
          );
          onCheckout();
        }
      }
    } catch (err) {
      console.error("Checkout error:", err);
      await showAppAlert("Не удалось оформить заказ. Попробуйте позже.");
    } finally {
      placingOrderRef.current = false;
      setIsPlacingOrder(false);
      if (showTgBtnProgress) {
        tg?.MainButton.hideProgress?.();
      }
    }
  };

  return (
    <div className="cart-summary">
      {showPhoneModal &&
        activePlatform !== "web" &&
        !isLoadingUser &&
        !hasPhoneInDB && (
          <div className="phone-modal-overlay">
            <div className="phone-modal">
              <h3 className="phone-modal__title">📱 Номер телефона</h3>
              <p className="phone-modal__text">
                Для оформления заказа нужен номер телефона
              </p>
              <div className="phone-modal__buttons">
                <button
                  className="phone-modal__btn phone-modal__btn--primary"
                  onClick={handleRequestNativePhone}
                  disabled={isRequestingPhone}
                >
                  {isRequestingPhone ? "Запрос..." : "Поделиться номером"}
                </button>
                <button
                  className="phone-modal__btn phone-modal__btn--secondary"
                  onClick={handleContinueManual}
                >
                  Ввести вручную
                </button>
              </div>
            </div>
          </div>
        )}

      <div className="cart-summary__header" onClick={handleHeaderClick}>
        <div className="cart-summary__left">
          <span className="cart-summary__total-label">Итого:</span>
          <span className="cart-summary__total-amount">
            {formatPrice(finalAmount)}
          </span>
          <span className="cart-summary__items-count">
            {itemsCount}{" "}
            {itemsCount === 1 ? "товар" : itemsCount < 5 ? "товара" : "товаров"}
          </span>
        </div>

        <button
          className={`cart-summary__action-btn ${
            isBelowMinOrder ? "cart-summary__action-btn--warning" : ""
          }`}
          onClick={handleToggleClick}
        >
          {showForm
            ? "Свернуть"
            : isBelowMinOrder
            ? `Ещё ${formatPrice(remainingForMinOrder)}`
            : "Оформить"}
        </button>
      </div>

      <div
        className={`cart-summary__form-wrapper ${
          isAnimating ? "cart-summary__form-wrapper--open" : ""
        }`}
      >
        {showForm && (
          <div className="cart-summary__form">
            <div className="cart-summary__fields">
              <div className="cart-summary__delivery-type">
                <button
                  type="button"
                  className={`cart-summary__delivery-type-btn ${
                    isPickup ? "cart-summary__delivery-type-btn--active" : ""
                  }`}
                  onClick={() => setDeliveryType("pickup")}
                >
                  Самовывоз
                </button>
                <button
                  type="button"
                  className={`cart-summary__delivery-type-btn ${
                    !isPickup ? "cart-summary__delivery-type-btn--active" : ""
                  } ${
                    onlyPickup
                      ? "cart-summary__delivery-type-btn--disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (!onlyPickup) setDeliveryType("delivery");
                  }}
                  disabled={onlyPickup}
                  aria-disabled={onlyPickup}
                >
                  Доставка
                </button>
              </div>
              {onlyPickup && (
                <p className="cart-summary__only-pickup-hint">
                  Для выбранной точки доступен только самовывоз
                </p>
              )}

              <input
                type="text"
                name="fullName"
                placeholder="ФИО *"
                value={formData.fullName}
                onChange={handleInputChange}
                className="cart-summary__input"
              />

              <input
                ref={phoneInputRef}
                type="tel"
                name="phone"
                placeholder="Телефон * (+7 ___ ___-__-__)"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handlePhoneBlur}
                className={`cart-summary__input ${
                  !isValidPhone && formData.phone
                    ? "cart-summary__input--error"
                    : ""
                }`}
              />

              {!isPickup && (districtsLoading || districts.length > 0 || districtsError) && (
                <div className="cart-summary__district-field">
                  <label
                    className="cart-summary__district-label"
                    htmlFor="checkout-district"
                  >
                    Район города{districts.length > 0 ? " *" : ""}
                  </label>

                  {districtsLoading ? (
                    <div className="cart-summary__district-status">
                      Загрузка районов...
                    </div>
                  ) : districtsError ? (
                    <div className="cart-summary__district-error">
                      <span>{districtsError}</span>
                      <button
                        type="button"
                        className="cart-summary__district-retry"
                        onClick={() => void loadDistricts()}
                      >
                        Повторить
                      </button>
                    </div>
                  ) : (
                    <select
                      id="checkout-district"
                      name="district"
                      value={selectedDistrictId}
                      onChange={(e) => setSelectedDistrictId(e.target.value)}
                      className={`cart-summary__select ${
                        requiresDistrict && !selectedDistrictId
                          ? "cart-summary__select--error"
                          : ""
                      }`}
                    >
                      <option value="">Выберите район</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {!isPickup && (
                <input
                  ref={addressInputRef}
                  type="text"
                  name="address"
                  placeholder="Адрес доставки *"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="cart-summary__input"
                />
              )}

              {!isPickup && (
                <textarea
                  name="comment"
                  placeholder="Комментарий (этаж, домофон)"
                  value={formData.comment}
                  onChange={handleInputChange}
                  className="cart-summary__textarea"
                  rows={2}
                />
              )}
            </div>

            {!isPickup && (
              <div className="cart-summary__delivery-policy">
                <div className="cart-summary__delivery-policy-head">
                  <span className="cart-summary__delivery-policy-title">
                    Доставка
                  </span>
                  <span
                    className="cart-summary__delivery-courier-note"
                    title="Сумма заказа не включает оплату курьеру"
                  >
                    Оплачивается отдельно курьеру
                  </span>
                </div>

                <button
                  type="button"
                  className="cart-summary__delivery-toggle"
                  aria-expanded={deliveryInfoOpen}
                  onClick={() => setDeliveryInfoOpen((v) => !v)}
                >
                  <span>Тарифы и условия</span>
                  <span className="cart-summary__delivery-toggle-chevron" aria-hidden>
                    {deliveryInfoOpen ? "▲" : "▼"}
                  </span>
                </button>

                {deliveryInfoOpen && (
                  <div className="cart-summary__delivery-details">
                    <p className="cart-summary__delivery-details-intro">
                      Стоимость доставки ягоды и фруктов по городу:
                    </p>
                    <ul className="cart-summary__delivery-tier-list">
                      {deliveryCfg.berryBoxTiers.map((tier, idx) => (
                        <li key={idx} className="cart-summary__delivery-tier-row">
                          <span>{formatBerryBoxesLabel(tier)}</span>
                          <span>{formatPrice(tier.priceRub)}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="cart-summary__delivery-muted">
                      Отдалённые районы — по стоимости такси
                    </p>

                    <p className="cart-summary__delivery-subhead">
                      Стоимость доставки морепродуктов
                    </p>
                    <ul className="cart-summary__delivery-fish-list">
                      <li>
                        По городу {formatPrice(deliveryCfg.cityDeliveryRub)}
                      </li>
                      <li className="cart-summary__delivery-em">
                        Бесплатная доставка от{" "}
                        {formatPrice(deliveryCfg.freeDeliveryFromRub)}
                      </li>
                      <li>
                        Доставка охлаждённой форели (коробками):{" "}
                        {formatPrice(deliveryCfg.troutFirstBoxRub)}
                        <span className="cart-summary__delivery-muted">
                          {" "}
                          · последующая коробка +
                          {formatPrice(deliveryCfg.troutExtraBoxRub)}
                        </span>
                        <span className="cart-summary__delivery-muted">
                          {" "}
                          · подъём в квартиру +
                          {formatPrice(deliveryCfg.troutLiftPerBoxRub)} за коробку
                        </span>
                      </li>
                    </ul>

                    <p
                      className="cart-summary__delivery-warning"
                      title="Укажите актуальный телефон и будьте на связи в день доставки"
                    >
                      Если курьер не смог дозвониться, доставку не сможем
                      осуществить
                    </p>
                  </div>
                )}

                <p className="cart-summary__delivery-split-note">
                  Доставка ягод и фруктов и доставка морепродуктов (охлаждённой
                  форели, прочей продукции) осуществляются в разные дни.
                </p>
              </div>
            )}

            <div className="cart-summary__payment-method">
              <span className="cart-summary__payment-method-label">
                Способ оплаты
              </span>
              <div className="cart-summary__delivery-type">
                <button
                  type="button"
                  className={`cart-summary__delivery-type-btn ${
                    isOnlinePayment
                      ? "cart-summary__delivery-type-btn--active"
                      : ""
                  }`}
                  onClick={() => setPaymentMethod("yookassa")}
                >
                  Онлайн-оплата
                </button>
                <button
                  type="button"
                  className={`cart-summary__delivery-type-btn ${
                    !isOnlinePayment
                      ? "cart-summary__delivery-type-btn--active"
                      : ""
                  }`}
                  onClick={() => setPaymentMethod("cash")}
                >
                  {cashPaymentLabel}
                </button>
              </div>
            </div>

            <button
              className={`cart-summary__submit-btn ${
                !isFormValid || isBelowMinOrder
                  ? "cart-summary__submit-btn--disabled"
                  : ""
              }`}
              onClick={handleCheckout}
              disabled={
                isPlacingOrder ||
                isCheckingOut ||
                !isFormValid ||
                isBelowMinOrder
              }
            >
              {isPlacingOrder || isCheckingOut ? (
                <span className="cart-summary__spinner"></span>
              ) : isOnlinePayment ? (
                `Оплатить ${formatPrice(finalAmount)}`
              ) : (
                `Оформить заказ · ${formatPrice(finalAmount)}`
              )}
            </button>

            <button
              type="button"
              className="cart-summary__secondary-btn"
              onClick={handleClearCartAndGoHome}
            >
              Очистить корзину и на главную
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
